/**
 * Overdue Callback Queue — Phase 1c (DERIVED selector)
 * ─────────────────────────────────────────────────────
 * No new storage. Computes which callbacks are overdue based on
 * severity-tiered SLA windows. Surfacing is mode-agnostic; only the
 * *escalation* on overdue items differs between soft and hard mode
 * (advisory vs actioned), which is the suggestion writer's job.
 */

import type { Callback } from "./callback";
import type { DisputeEvent } from "./dispute/dispute";
import { deriveAllDisputes } from "./dispute/dispute";
import type { HoldbackLedgerEntry } from "./holdback/ledger";
import { deriveAllHoldbackStates } from "./holdback/deriveState";

const SLA_HOURS_BY_SEVERITY: Record<Callback["severity"], number> = {
  high: 24,
  medium: 72,
  low: 168, // 7d
};

export interface OverdueCallbackEntry {
  callback: Callback;
  hoursOverdue: number;
  slaHours: number;
  hasOpenDispute: boolean;
  hasActiveHoldback: boolean;
}

export interface BuildOverdueQueueInput {
  callbacks: Callback[];
  disputeEvents: DisputeEvent[];
  holdbackLedger: HoldbackLedgerEntry[];
  nowIso?: string;
}

export function getOverdueCallbackQueue(
  input: BuildOverdueQueueInput,
): OverdueCallbackEntry[] {
  const now = input.nowIso ? Date.parse(input.nowIso) : Date.now();
  const openDisputes = new Set(
    deriveAllDisputes(input.disputeEvents)
      .filter((d) => d.status === "open" && d.callbackId)
      .map((d) => d.callbackId as string),
  );
  const activeHoldbackCallbackIds = new Set(
    deriveAllHoldbackStates(input.holdbackLedger)
      .filter((s) => s.status === "active" || s.status === "paused")
      .flatMap((s) => s.entries.map((e) => e.sourceCallbackId).filter(Boolean) as string[]),
  );

  const overdue: OverdueCallbackEntry[] = [];
  for (const cb of input.callbacks) {
    if (cb.attributedTo === "owner") continue; // owner-attributed callbacks don't carry executor SLA
    const slaHours = SLA_HOURS_BY_SEVERITY[cb.severity];
    const ageHours = (now - Date.parse(cb.filedAt)) / (1000 * 60 * 60);
    if (ageHours <= slaHours) continue;
    overdue.push({
      callback: cb,
      hoursOverdue: ageHours - slaHours,
      slaHours,
      hasOpenDispute: openDisputes.has(cb.id),
      hasActiveHoldback: activeHoldbackCallbackIds.has(cb.id),
    });
  }
  // Most overdue first.
  return overdue.sort((a, b) => b.hoursOverdue - a.hoursOverdue);
}

export const OVERDUE_SLA_HOURS = SLA_HOURS_BY_SEVERITY;
